import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscountStatusComment } from 'interfaces';
import { formatDate, formatTime, formatTextWithUrls, parseDate } from 'utils';
import { Text } from 'view/atoms';
import { ModalWindow } from 'view/molecules/ModalWindow';
import { STATUS_COMMENT_LABEL_MAP } from './StatusCommentsModal.constants';
import * as S from './StatusCommentsModal.styles';

type Props = {
  comments: DiscountStatusComment[];
  closeHandler: () => void;
  'data-testid'?: string;
};

export const StatusCommentsModal = ({
  comments,
  closeHandler,
  'data-testid': dataTestId = 'status-comments-modal',
}: Props) => {
  const { t } = useTranslation();

  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) =>
          parseDate(b.createdAt).getTime() - parseDate(a.createdAt).getTime(),
      ),
    [comments],
  );

  return (
    <ModalWindow
      hideWindow={closeHandler}
      contentStyles={S.ModalStyles}
      data-testid={dataTestId}
    >
      <>
        <S.Title>
          <Text kind='h2'>{t('archives.statusComments.title')}</Text>
        </S.Title>

        <S.CommentsList>
          {sortedComments.map((comment) => (
            <S.CommentCard key={comment.id} data-testid='status-comment-item'>
              <S.CommentText>
                <S.CommentLabel>
                  {t(STATUS_COMMENT_LABEL_MAP[comment.transitionType])}
                </S.CommentLabel>{' '}
                {formatTextWithUrls(comment.commentText, { asLink: true })}
              </S.CommentText>

              <S.CommentDateTime>
                <span>{formatDate(comment.createdAt)}</span>
                <span>{formatTime(comment.createdAt)}</span>
              </S.CommentDateTime>
            </S.CommentCard>
          ))}
        </S.CommentsList>
      </>
    </ModalWindow>
  );
};
